/**
 * 기구 무대(machine-stage · PICNIC-SB-FIELD-001 §6.1 · 2026-10-06) — 화면 규칙(순수 함수).
 *  - 무대 고르기: 미션 목표 > 내 기계 모형과 같은 무대 > 첫 무대
 *  - 목표 한 줄 값 · 신남(손님 몸짓) · 별 변화 → 반응
 */
import type { ModeDefinition, StageFrame } from "@picnic-field/local-host/protocol";

export const STAGE_MODE_ID = "machine-stage";

export { STAGE_MODELS, isStageMachine } from "../../../lib/stageMachine";

/** '센서 손' 패널 입력(모드 정의 stage.inputs) */
export type StageInput = "buttons" | "pitch" | "roll" | "force" | "color" | "distance";

interface StagePresetLike {
  readonly key: string;
  readonly stage?: {
    readonly lesson: string;
    readonly model: string;
    readonly scene: string;
    readonly inputs?: readonly StageInput[];
    /** 별 목표 — 화면 문구(label · 운영 콘솔 무대 목표 편집기 2026-10-08)만 본다 */
    readonly goals?: readonly { readonly label?: { readonly ko?: string; readonly en?: string } }[];
  };
}

export const stagePresets = (mode: ModeDefinition | null): readonly StagePresetLike[] =>
  ((mode?.presets ?? []) as readonly StagePresetLike[]).filter((p) => !!p.stage);

/** 처음 고를 무대 — 미션 프리셋(있고 무대에 있으면) > 내 기계 모형의 무대 > 첫 무대 */
export function pickStage(
  presets: readonly StagePresetLike[],
  missionKey: string | null,
  modelSlug: string | null | undefined,
): string | null {
  if (missionKey && presets.some((p) => p.key === missionKey)) return missionKey;
  const byModel = modelSlug ? presets.find((p) => p.stage?.model === modelSlug) : undefined;
  return byModel?.key ?? presets[0]?.key ?? null;
}

/**
 * 학생 카탈로그 stage_scene(GET /api/catalog — 활성만) → 프리셋 키 → 꾸밈 payload. 거르기는 렌더러(normalizeStageDecor)가 한다.
 * {items:[…]} · 배열 둘 다 받는다.
 */
export function readStageDecor(v: unknown): Map<string, unknown> {
  const o = v as { items?: unknown } | null;
  const list: unknown[] = Array.isArray(v) ? v : Array.isArray(o?.items) ? o.items : [];
  const out = new Map<string, unknown>();
  for (const raw of list) {
    const r = (raw ?? {}) as Record<string, unknown>;
    if (r.type != null && r.type !== "stage_scene") continue;
    if (r.enabled === false || typeof r.key !== "string" || r.payload == null) continue;
    out.set(r.key, r.payload);
  }
  return out;
}

/** 운영자가 올린 무대 3D 모델(카탈로그 stage_model) 한 개 */
export interface StageModelEntry {
  readonly key: string;
  /** GLB sha256(GET /api/stage-models/{sha}) */
  readonly glb: string;
  /** 기본 키(m) */
  readonly height: number;
  /** 인벤토리 이름(i18n.ko) */
  readonly name: string;
}

/** 카탈로그 stage_model(활성만) → 모델 키 → 항목. 모양이 틀린 것은 뺀다 */
export function readStageModels(v: unknown): Map<string, StageModelEntry> {
  const o = v as { items?: unknown } | null;
  const list: unknown[] = Array.isArray(v) ? v : Array.isArray(o?.items) ? o.items : [];
  const out = new Map<string, StageModelEntry>();
  for (const raw of list) {
    const r = (raw ?? {}) as Record<string, unknown>;
    if (r.type != null && r.type !== "stage_model") continue;
    if (r.enabled === false || typeof r.key !== "string") continue;
    const p = (r.payload ?? {}) as Record<string, unknown>;
    if (typeof p.glb !== "string" || !/^[0-9a-f]{64}$/.test(p.glb)) continue;
    const height = typeof p.height === "number" && p.height > 0 ? p.height : 0.5;
    const i18n = (r.i18n ?? {}) as Record<string, unknown>;
    const name = typeof i18n.ko === "string" && i18n.ko.trim() ? i18n.ko : r.key;
    out.set(r.key, { key: r.key, glb: p.glb, height, name });
  }
  return out;
}

/** sha → blob URL(한 번 받은 파일은 이 탭 동안 다시 받지 않는다 — 내용 주소라 바뀌지 않는다) */
const modelUrls = new Map<string, Promise<string | null>>();

/**
 * 렌더러 stageModelUrl — 꾸밈의 모델 키 → 받은 GLB 의 blob URL(Bearer 요청이라 렌더러가 직접 받지 않는다). 모르는 키 · 실패는 null(그 모델만 빠진다).
 */
export function stageModelUrlFor(
  models: ReadonlyMap<string, StageModelEntry>,
  fetchBytes: (sha256: string) => Promise<Uint8Array>,
): (key: string) => Promise<string | null> {
  return (key) => {
    const m = models.get(key);
    if (!m) return Promise.resolve(null);
    let p = modelUrls.get(m.glb);
    if (!p) {
      p = fetchBytes(m.glb)
        .then((b) => URL.createObjectURL(new Blob([b as BlobPart], { type: "model/gltf-binary" })))
        .catch(() => {
          modelUrls.delete(m.glb);
          return null;
        });
      modelUrls.set(m.glb, p);
    }
    return p;
  };
}

/** 별 목표 i 번째(0 부터) 운영자 문구 — 그 언어가 없으면 null(셸 번역을 쓴다) */
export function goalLabel(
  preset: StagePresetLike | null,
  i: number,
  locale: string,
): string | null {
  const l = preset?.stage?.goals?.[i]?.label;
  if (!l) return null;
  const v = locale.startsWith("en") ? l.en : l.ko;
  return v?.trim() ? v : null;
}

/** 'sp-d08' → i18n 열쇠 'spD08' */
export const lessonKey = (presetKey: string): string =>
  presetKey.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

/** 별이 늘었나 · 보너스를 새로 찾았나 — 반응(손님 · 소리 · 피코) */
export function stageChange(
  prev: StageFrame | null,
  next: StageFrame,
): { star: number | null; bonus: boolean; note: string | null } {
  const star = next.stars > (prev?.stars ?? 0) ? next.stars : null;
  const bonus = !!next.bonus?.done && !prev?.bonus?.done;
  const note =
    next.notes.length && next.notes.length !== (prev?.notes.length ?? 0)
      ? next.notes[next.notes.length - 1]!
      : null;
  return { star, bonus, note };
}

/** 신남 0~1 — 주 관절이 빠를수록(도는 관절 720°/s · 미끄럼 30cm/s 에서 1) */
export function exciteOf(f: StageFrame | null): number {
  const r = Math.abs(f?.main?.rate ?? 0);
  return Math.min(1, r / 720);
}

/** 목표 진행 칸(0~1) */
export const goalFill = (g: { value: number; target: number; done: boolean }): number =>
  g.done ? 1 : Math.max(0, Math.min(1, g.target > 0 ? g.value / g.target : 0));

/** 화면에 보일 값(바퀴는 소수 한 자리 · 나머지는 정수) */
export function goalValue(g: {
  measure: string;
  value: number;
  target: number;
  done: boolean;
}): string {
  const v = g.done ? Math.max(g.value, g.target) : g.value;
  const shown =
    g.measure === "turns" || g.measure === "hold" ? Math.floor(v * 10) / 10 : Math.floor(v);
  return `${Math.min(shown, g.target)}`;
}
